/* eslint-disable @typescript-eslint/no-explicit-any */
import lodashGet from "lodash/get";

export type CoAuthorData = {
  profileId: string | null;
  name: string;
  affiliation: string;
  position: number;
};

export type CoAuthorBlockTemplateUtilOptions = {
  coAuthorArrayPath: string;
  authorFullNamePath?: string;
  authorFirstNamePath?: string;
  authorLastNamePath?: string;
  authorProfileIdPath: string;
  authorAffiliationPath: string;
  authorSeperator?: string;
  profileNeedsStylingPath?: string;
  profileNeedsStylingClassName?: string;
  profileNeedsIndicatorBeforeName?: string;
  profileNeedsIndicatorAfterName?: string;
  legendSeperator?: string;
  positionPath?: string;
  blockClassName?: string;
  noDataMessage?: string;
  output?: "full" | "namesOnly" | "affiliationsOnly";
  disabledAffiliationSup?: "true" | "false";
  data: any;
};

export const coAuthorBlockTemplateUtil = ({
  coAuthorArrayPath,
  authorFullNamePath,
  authorFirstNamePath,
  authorLastNamePath,
  authorProfileIdPath,
  authorAffiliationPath,
  authorSeperator = ", ",
  profileNeedsStylingPath,
  profileNeedsStylingClassName,
  profileNeedsIndicatorBeforeName = "",
  profileNeedsIndicatorAfterName = "",
  legendSeperator = ", ",
  positionPath,
  blockClassName,
  output = "full",
  noDataMessage = "",
  disabledAffiliationSup = "false",
  data,
}: CoAuthorBlockTemplateUtilOptions) => {
  let html = "";
  const authorArray: CoAuthorData[] = [];
  const affiliations: string[] = [];

  const coAuthorsData = lodashGet(data, coAuthorArrayPath);
  if (
    coAuthorsData == undefined ||
    !Array.isArray(coAuthorsData) ||
    coAuthorsData.length === 0
  ) {
    return noDataMessage;
  }

  const profileIdForStyling = profileNeedsStylingPath
    ? lodashGet(data, profileNeedsStylingPath)
    : undefined;

  coAuthorsData.forEach((e: CoAuthorData, index: number) => {
    const name = authorFullNamePath
      ? lodashGet(e, authorFullNamePath)
      : `${lodashGet(e, authorFirstNamePath ?? "")} ${lodashGet(
          e,
          authorLastNamePath ?? "",
        )}`.trim();

    const positionVal = positionPath ? lodashGet(e, positionPath) : undefined;
    const position =
      positionVal != null && !isNaN(Number(positionVal))
        ? Number(positionVal)
        : index;

    authorArray.push({
      name,
      affiliation: lodashGet(e, authorAffiliationPath) ?? "",
      profileId: lodashGet(e, authorProfileIdPath) ?? null,
      position,
    });
  });

  authorArray.sort((a, b) => a.position - b.position);

  let namesOutput = "";
  authorArray.forEach((author, index) => {
    if (!author.name) {
      return;
    }

    let affiliationIndex = author.affiliation
      ? affiliations.indexOf(author.affiliation)
      : -1;

    if (author.affiliation && affiliationIndex === -1) {
      affiliations.push(author.affiliation);
      affiliationIndex = affiliations.length - 1;
    }

    const profileNeedsStyling =
      profileIdForStyling && author.profileId === profileIdForStyling;
    let nameStringAssembled = "";
    if (index > 0) {
      nameStringAssembled += authorSeperator;
    }
    if (profileNeedsStyling && profileNeedsStylingClassName != "") {
      nameStringAssembled += `<span class="${profileNeedsStylingClassName}">`;
    } else {
      nameStringAssembled += "<span>";
    }
    if (profileNeedsStyling && profileNeedsIndicatorBeforeName) {
      nameStringAssembled += profileNeedsIndicatorBeforeName;
    }
    nameStringAssembled += author.name;
    if (
      author.affiliation &&
      affiliationIndex > -1 &&
      disabledAffiliationSup === "false"
    ) {
      nameStringAssembled += `<sup>${affiliationIndex + 1}</sup>`;
    }
    if (profileNeedsStyling && profileNeedsIndicatorAfterName) {
      nameStringAssembled += profileNeedsIndicatorAfterName;
    }
    nameStringAssembled += "</span>";
    namesOutput += nameStringAssembled;
  });

  let legendOutput = "";
  if (affiliations.length > 0) {
    affiliations.forEach((aff, index) => {
      legendOutput += `${aff}<sup>${index + 1}</sup>`;
      if (index < affiliations.length - 1) {
        legendOutput += legendSeperator;
      }
    });
  }

  html = `
    <div data-testid="coauthor-block" class="p-3 flex flex-col justify-start items-start ${blockClassName ?? ""}">
      <div data-testid="coauthor-block-names" class="text-sm mb-2">${namesOutput}</div>
      <div data-testid="coauthor-block-affiliation" class="w-full text-sm mt-2">${legendOutput}</div>
    </div>
  `;
  if (output === "namesOnly") {
    html = namesOutput;
  } else if (output === "affiliationsOnly") {
    html = legendOutput;
  }

  return html;
};
